import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Drawer,
  Empty,
  Modal,
  Row,
  Segmented,
  Space,
  Typography,
  message,
} from "antd";
import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import DynamicFormRenderer from "../../clinicalForms/DynamicFormRenderer";
import DynamicFormSummary from "../../clinicalForms/DynamicFormSummary";
import NoDataPage from "../../NoDataPages/NoDataPage";
import EditIcon from "../../ui/EditIcon";
import btnStyles from "../../../styles/Custom/Button.module.css";
import assessmentIcon from "../../../assets/images/nurseIcons/assessment.svg";
import noReview from "../../../assets/doctorIcons/noReview.svg";
import {
  createClinicalFormSubmissionApi,
  listClinicalFormSubmissionsApi,
  updateClinicalFormSubmissionApi,
} from "../../../services/clinicalForms.services";
import { admissionQuery, isActiveAdmissionStatus } from "./admissionContext";
import useQuery from "../../../hooks/useQuery";

const safeParse = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const submissionMeta = (s) => {
  const when = s?.updated_at || s?.created_at;
  return {
    stamp: when ? dayjs(when).format("DD/MM/YYYY hh:mm A") : null,
    segmentLabel: when
      ? `${dayjs(when).format("MMM DD")}, ${dayjs(when).format("h:mm A")}`
      : "Draft",
    who: s?.submitted_by_name || null,
  };
};

/**
 * Renders a single clinical form for a patient admission — matches the
 * visual chrome of the static built-in role IA/Review pages: NoDataPage
 * empty state, Tags row + Edit button when a submission exists, and a
 * full-width Drawer for form editing.
 */
const ClinicalFormPanel = ({
  form,
  admissionId,
  admissionType,
  readOnly = false,
  // Same as static roles: discard / unsaved / required checks only on IA & Review
  confirmExit = false,
}) => {
  const formId = form?.id;
  const isRepeatable = !!form?.repeatable;
  const query = useQuery();
  const isActive = isActiveAdmissionStatus(admissionType, query?.status);
  const canMutate = !readOnly && isActive;

  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [viewId, setViewId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isDirtyRef = useRef(false);

  const load = async () => {
    if (!formId || !admissionId) return;
    setLoading(true);
    setError(null);
    try {
      const subsRes = await listClinicalFormSubmissionsApi({
        form: formId,
        ...admissionQuery(admissionType, admissionId),
      });
      const rawSubs = Array.isArray(subsRes?.data) ? subsRes.data : [];
      const subs = [...rawSubs].sort((a, b) => {
        const ta = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const tb = new Date(b?.updated_at || b?.created_at || 0).getTime();
        return tb - ta;
      });
      setSubmissions(subs);
      return subs;
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load submissions");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, admissionId, admissionType]);

  useEffect(() => {
    if (!isRepeatable || submissions.length === 0) return;
    setViewId((prev) => {
      if (prev != null && submissions.some((s) => String(s.id) === String(prev))) {
        return prev;
      }
      return submissions[0]?.id ?? null;
    });
  }, [submissions, isRepeatable]);

  const segmentOptions = useMemo(
    () =>
      submissions.map((s) => ({
        label: submissionMeta(s).segmentLabel,
        value: s.id,
      })),
    [submissions]
  );

  const viewedSubmission = useMemo(
    () => submissions.find((s) => String(s.id) === String(viewId)) || null,
    [submissions, viewId]
  );

  const openCreate = () => {
    setActiveId(null);
    setValues({});
    setDrawerOpen(true);
  };

  const openEdit = (s) => {
    setActiveId(s.id);
    setValues(safeParse(s.data));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActiveId(null);
    setValues({});
    isDirtyRef.current = false;
  };

  // Match existing IA/Review ClosePopup. Other Forms close immediately.
  const handleBackButton = () => {
    if (!confirmExit) {
      closeDrawer();
      return;
    }
    Modal.confirm({
      title: "Discard changes",
      content: "Are you sure to discard changes?",
      onOk: closeDrawer,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { data: JSON.stringify(values) };
      if (activeId) {
        await updateClinicalFormSubmissionApi(activeId, payload);
        message.success("Form updated");
      } else {
        await createClinicalFormSubmissionApi({
          form: formId,
          ...admissionQuery(admissionType, admissionId),
          ...payload,
        });
        message.success("Form submitted");
      }
      const subs = await load();
      closeDrawer();      if (isRepeatable) {
        setViewId(activeId || subs[0]?.id || null);
      }
    } catch (e) {
      message.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Empty description="Form not configured" />;
  if (loading) return <LoadingIndicator message="Loading form" />;
  if (error) return <Alert type="error" message={error} showIcon />;

  const currentSubmission = isRepeatable ? null : submissions[0] || null;
  const currentMeta = currentSubmission
    ? submissionMeta(currentSubmission)
    : null;
  const currentValues = currentSubmission
    ? safeParse(currentSubmission.data)
    : {};

  const emptyTitle = canMutate
    ? `${form.name} Not Yet Submitted`
    : `${form.name || "Review Form"} Not Available`;
  const emptySubtitle = canMutate
    ? `Kindly, upload a ${form.name?.toLowerCase()} here`
    : null;
  const primaryLabel = form.name;

  const renderEmpty = () => (
    <Row
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
      }}
    >
      <Col span={24} style={{ textAlign: "center" }}>
        <NoDataPage
          title={emptyTitle}
          subtitle={emptySubtitle}
          image={assessmentIcon}
        />
        {canMutate && (
          <Button
            type="primary"
            onClick={openCreate}
            style={{ fontWeight: 500 }}
            className={`${btnStyles.lgBtn} btn-scale clinical-form-name-btn`}
          >
            {primaryLabel}
          </Button>
        )}
      </Col>
    </Row>
  );

  const renderNonRepeatableFilled = () => (
    <Row gutter={[12, 8]} style={{ marginTop: 0, alignItems: "center" }}>
      <Col span={24}>
        <Typography.Text
          strong
          style={{ color: "#101828", fontSize: 16 }}
        >
          {form.name}
        </Typography.Text>
      </Col>
      <Col
        xs={24}
        md={16}
        style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}
      >
        {currentMeta?.who && (
          <Typography.Text type="secondary">
            Submitted by:{" "}
            <Typography.Text strong style={{ color: "#101828" }}>
              {currentMeta.who}
            </Typography.Text>
          </Typography.Text>
        )}
        {currentMeta?.stamp && (
          <Typography.Text type="secondary">
            Updated at:{" "}
            <Typography.Text strong style={{ color: "#101828" }}>
              {currentMeta.stamp}
            </Typography.Text>
          </Typography.Text>
        )}
      </Col>
      <Col xs={24} md={8} style={{ textAlign: "right" }}>
        {canMutate && (
          <Button
            type="primary"
            onClick={() => openEdit(currentSubmission)}
            className={`${btnStyles.lgBtn} inv-btn`}
            style={{ fontWeight: 500 }}
          >
            <EditIcon />
            Edit
          </Button>
        )}
      </Col>
      <Col span={24}>
        <DynamicFormSummary
          sections={form.sections}
          values={currentValues}
        />
      </Col>
    </Row>
  );

  const renderRepeatableEmpty = () => (
    <Row
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
      }}
    >
      <Col span={24} style={{ textAlign: "center" }}>
        <NoDataPage
          title={emptyTitle}
          subtitle={emptySubtitle}
          image={noReview}
        />
        {canMutate && (
          <Button
            type="primary"
            onClick={openCreate}
            className={`${btnStyles.lgBtn} inv-btn clinical-form-name-btn`}
            style={{ fontWeight: 500 }}
          >
            {primaryLabel}
          </Button>
        )}
      </Col>
    </Row>
  );

  const renderRepeatableFilled = () => {
    const viewedValues = viewedSubmission
      ? safeParse(viewedSubmission.data)
      : {};
    const viewedMeta = viewedSubmission
      ? submissionMeta(viewedSubmission)
      : null;

    return (
      <Row gutter={[12, 12]} style={{ marginTop: 0 }}>
        <Col span={24}>
          <Typography.Text
            strong
            style={{ color: "#101828", fontSize: 16 }}
          >
            {form.name}
          </Typography.Text>
        </Col>
        <Col span={24}>
          <Space
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              {canMutate && (
                <Button
                  type="primary"
                  onClick={openCreate}
                  className={`${btnStyles.lgBtn} inv-btn clinical-form-name-btn`}
                  style={{ fontWeight: 500 }}
                >
                  <PlusOutlined /> {form.name || "Add"}
                </Button>
              )}
              <Segmented
                className="segment-wrap"
                value={viewId}
                options={segmentOptions}
                onChange={setViewId}
                size="middle"
                style={{
                  boxShadow: "2px 2px 3px #e3e4e6",
                  maxWidth: "100%",
                }}
              />
            </div>
            {canMutate && (
              <Button
                type="primary"
                onClick={() => viewedSubmission && openEdit(viewedSubmission)}
                className={`${btnStyles.lgBtn} inv-btn`}
                disabled={!viewedSubmission}
                style={{ fontWeight: 500 }}
              >
                <EditIcon />
                Edit
              </Button>
            )}
          </Space>
        </Col>
        {viewedMeta?.who && (
          <Col span={24}>
            <Typography.Text type="secondary">
              Submitted by:{" "}
              <Typography.Text strong style={{ color: "#101828" }}>
                {viewedMeta.who}
              </Typography.Text>
            </Typography.Text>
          </Col>
        )}
        <Col span={24}>
          <DynamicFormSummary sections={form.sections} values={viewedValues} />
        </Col>
      </Row>
    );
  };

  const body = (() => {
    if (isRepeatable) {
      return submissions.length === 0
        ? renderRepeatableEmpty()
        : renderRepeatableFilled();
    }
    return currentSubmission ? renderNonRepeatableFilled() : renderEmpty();
  })();

  return (
    <>
      {body}
      <Drawer
        className="clinical-form-drawer"
        open={drawerOpen}
        width="100%"
        onClose={handleBackButton}
        closeIcon={null}
        keyboard={false}
        styles={{
          body: {
            overflow: "hidden",
            paddingTop: 0,
            paddingBottom: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
        title={
          <Row align="middle" gutter={[8, 8]}>
            <Col style={{ display: "flex", alignItems: "center" }}>
              <LeftOutlined
                style={{ fontSize: 20, cursor: "pointer" }}
                onClick={handleBackButton}
              />
            </Col>
            <Col flex="auto">
              <Typography.Text
                style={{ color: "#101828", fontSize: 16, fontWeight: 600 }}
              >
                {form.name}
              </Typography.Text>
            </Col>
          </Row>
        }
        footer={null}
      >
        <div className="clinical-form-drawer-content">
          <DynamicFormRenderer
            sections={form.sections}
            values={values}
            onChange={setValues}
            readOnly={readOnly}
            onSubmit={save}
            saving={saving}
            isEdit={!!activeId}
            admissionId={admissionId}
            admissionType={admissionType}
            isDirtyRef={isDirtyRef}
            confirmExit={confirmExit}
          />
        </div>
      </Drawer>
    </>
  );
};

export default ClinicalFormPanel;
